import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { MapPin, Utensils } from "lucide-react";

export default function OrderDialog({ dish, open, onOpenChange, onSuccess }) {
  const [portions, setPortions] = useState(1);
  const [needsCutlery, setNeedsCutlery] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!dish) return null;
  const maxP = Math.min(5, dish.portions_available);

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        dish_id: dish.id,
        portions,
        needs_cutlery: needsCutlery,
      });
      toast.success(`Prenotato ${dish.name} × ${portions}`);
      onSuccess?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="order-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Prenota {dish.name}</DialogTitle>
          <DialogDescription>
            € {Number(dish.price).toFixed(2)} a porzione · chiude alle {dish.cutoff_time}
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <label className="text-sm font-medium mb-2 block">Numero porzioni</label>
          <div className="flex gap-2">
            {Array.from({ length: maxP }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                onClick={() => setPortions(n)}
                data-testid={`portion-option-${n}`}
                className={`w-12 h-12 rounded-md border font-medium transition-all duration-200 ${
                  portions === n
                    ? "bg-primary text-white border-primary"
                    : "bg-white border-border hover:border-foreground/30"
                }`}
              >
                {n}
              </button>
            ))}
          </div>

          <label
            className="mt-4 p-3 bg-card border border-border rounded-md flex items-start gap-3 cursor-pointer hover:border-foreground/30 transition-colors"
            data-testid="order-cutlery-toggle"
          >
            <input
              type="checkbox"
              checked={needsCutlery}
              onChange={(e) => setNeedsCutlery(e.target.checked)}
              className="mt-0.5 accent-primary w-4 h-4"
              data-testid="order-cutlery-checkbox"
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium flex items-center gap-1.5">
                <Utensils className="w-3.5 h-3.5 text-primary" /> Ti servono le posate?
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Spunta se vuoi che il preparatore te le metta da parte (forchetta/coltello/tovagliolo).
              </div>
            </div>
          </label>

          <div className="mt-4 p-3 bg-muted rounded-md flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Totale</span>
            <span className="font-display font-semibold text-lg">
              € {(Number(dish.price) * portions).toFixed(2)}
            </span>
          </div>
          {dish.pickup_location && (
            <div className="mt-3 p-3 bg-primary/5 border border-primary/20 rounded-md flex items-start gap-2" data-testid="order-pickup-location">
              <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-0.5">Punto di ritiro</div>
                <div className="font-medium">{dish.pickup_location}</div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-order">
            Annulla
          </Button>
          <Button
            onClick={submit}
            disabled={loading || maxP < 1}
            data-testid="confirm-order"
            className="bg-primary hover:bg-[#436343]"
          >
            {loading ? "Prenoto…" : "Conferma"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
